import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { getVersion, subscribe } from 'valtio/vanilla';
import { DEVELOPMENT } from '../game/Constants';
import { noteUiWake as hitchNoteUiWake } from '../game/HitchLog';

/**
 * Binds a Lit component to the Valtio uiState proxy so that it re-renders when
 * a top-level key it reads changes, and only then.
 *
 * Usage (one line per component):
 * ```ts
 * import { uiState as uiStateProxy } from './UIState';
 * import { bindValtio } from './ValtioController';
 *
 * class Foo extends LitElement {
 *   private uiState = bindValtio(this, uiStateProxy);
 *   override render() { return html`<div>${this.uiState.foo}</div>`; }
 * }
 * ```
 *
 * The returned object is a thin view over the live proxy: every read goes
 * straight through, in render and in event handlers alike, so there is no
 * snapshot and no microtask lag. The view also records which top-level keys
 * the component has read, and the component is woken for changes under those
 * keys only. The keys are learnt as they are read and kept for as long as the
 * component is connected, so a branch that reads a new key subscribes to it
 * from then on, and nothing has to be declared or kept in step.
 *
 * Until 2026-10-20 every component held its own subscription to the root
 * proxy and re-rendered on every write anywhere: a scoreboard, a ping or a
 * chat line woke all forty-odd mounted components, lobby included, at 1.5-4 ms
 * a time. Now a single root subscription per proxy diffs the keys that have
 * readers, by identity for primitives and replaced objects and by Valtio's
 * subtree version for nested objects, so a write wakes the two or three
 * components that show it. Valtio 2.3 creates no ops unless
 * unstable_enableOp is called, which is why the diff reads versions rather
 * than paths; getVersion is a stable export.
 *
 * Writes still go through GameAPI; a write through the view is forwarded to
 * the proxy unchanged. The view is not the proxy itself: hand uiStateProxy,
 * never the view, to Valtio's own subscribe or snapshot. A component is woken
 * only for what it reads through its view, so a child shown a nested uiState
 * object as a property must still read that object through its own view; in
 * dev, a bound component that read nothing by the end of its first update is
 * reported in the console, since nothing will ever wake it.
 */
export function bindValtio<T extends object>(host: ReactiveControllerHost, proxy: T): T {
  return new ValtioController(host, proxy).view;
}

/** A bound component as the registry holds it: the host to wake. */
interface Reader {
  readonly host: ReactiveControllerHost;
}

/** A key with readers, and what it held when last checked. */
interface KeyEntry {
  value: unknown;
  version: number | undefined;
  readonly readers: Set<Reader>;
}

/** One per proxy root: the single subscription and the keys with readers. */
class Registry {
  private readonly keys = new Map<string, KeyEntry>();
  /** Readers that enumerated the root and so depend on every key. */
  private readonly everything = new Set<Reader>();
  private subscribed = false;

  constructor(private readonly root: object) {}

  addReader(key: string, reader: Reader): void {
    let entry = this.keys.get(key);
    if (entry === undefined) {
      const value = Reflect.get(this.root, key) as unknown;
      entry = { value, version: getVersion(value), readers: new Set() };
      this.keys.set(key, entry);
    }
    entry.readers.add(reader);
    this.ensureSubscribed();
  }

  addReaderOfEverything(reader: Reader): void {
    this.everything.add(reader);
    this.ensureSubscribed();
  }

  removeReader(keys: Iterable<string>, reader: Reader): void {
    for (const key of keys) {
      const entry = this.keys.get(key);
      if (entry === undefined) continue;
      entry.readers.delete(reader);
      if (entry.readers.size === 0) this.keys.delete(key);
    }
    this.everything.delete(reader);
  }

  /** Readers by key, for the dev console and the probes. */
  readers(): Record<string, string[]> {
    const out: Record<string, string[]> = {};
    for (const [key, entry] of this.keys) out[key] = [...entry.readers].map(readerName);
    if (this.everything.size > 0) out['*'] = [...this.everything].map(readerName);
    return out;
  }

  private ensureSubscribed(): void {
    if (this.subscribed) return;
    this.subscribed = true;
    // One listener for the root; the callback runs once per batch of writes, in a microtask
    subscribe(this.root, () => this.dispatch());
  }

  /** Wakes the readers of every key whose value or subtree version moved since the last batch. */
  private dispatch(): void {
    const woken = new Set<Reader>(this.everything);
    const changed: string[] = [];
    for (const [key, entry] of this.keys) {
      const value = Reflect.get(this.root, key) as unknown;
      const version = getVersion(value);
      if (Object.is(entry.value, value) && entry.version === version) continue;
      entry.value = value;
      entry.version = version;
      changed.push(key);
      for (const reader of entry.readers) woken.add(reader);
    }
    // The dev hitch log's context: what the interface was woken for in the frame
    hitchNoteUiWake(woken.size, changed);
    for (const reader of woken) reader.host.requestUpdate();
  }
}

const registries = new WeakMap<object, Registry>();

function registryFor(root: object): Registry {
  let registry = registries.get(root);
  if (registry === undefined) {
    registry = new Registry(root);
    registries.set(root, registry);
  }
  return registry;
}

function readerName(reader: Reader): string {
  return reader.host instanceof HTMLElement ? reader.host.tagName.toLowerCase() : 'host';
}

/** The keys with readers and the components reading each, for a proxy root; dev console and probe use. */
export function uiStateReaders(proxy: object): Record<string, string[]> {
  return registryFor(proxy).readers();
}

class ValtioController<T extends object> implements ReactiveController, Reader {
  /** The view the component reads through. */
  readonly view: T;
  private readonly registry: Registry;
  /** Top-level keys read so far. */
  private readonly keys = new Set<string>();
  /** Set once the component enumerated the proxy, after which every key counts. */
  private all = false;
  private connected = false;
  private everConnected = false;
  private firstUpdateChecked = false;

  constructor(
    readonly host: ReactiveControllerHost,
    proxy: T,
  ) {
    this.registry = registryFor(proxy);
    this.view = new Proxy(proxy, {
      get: (target, key) => {
        if (typeof key === 'string') this.record(key);
        return Reflect.get(target, key) as unknown;
      },
      has: (target, key) => {
        if (typeof key === 'string') this.record(key);
        return Reflect.has(target, key);
      },
      ownKeys: (target) => {
        this.recordAll();
        return Reflect.ownKeys(target);
      },
      // No receiver, so Valtio's set trap sees the proxy itself as the target of the write, as a direct write does
      set: (target, key, value: unknown) => Reflect.set(target, key, value),
    });
    host.addController(this);
  }

  hostConnected(): void {
    this.connected = true;
    for (const key of this.keys) this.registry.addReader(key, this);
    if (this.all) this.registry.addReaderOfEverything(this);
    // A component coming back after a spell disconnected missed the writes in between
    if (this.everConnected) this.host.requestUpdate();
    this.everConnected = true;
  }

  hostDisconnected(): void {
    this.connected = false;
    this.registry.removeReader(this.keys, this);
  }

  hostUpdated(): void {
    if (!DEVELOPMENT || this.firstUpdateChecked) return;
    this.firstUpdateChecked = true;
    // After updateComplete so reads in updated() and firstUpdated() count too
    void this.host.updateComplete.then(() => {
      if (this.keys.size === 0 && !this.all) {
        console.warn(
          `${readerName(this)} is bound to uiState but read none of it in its first update, so nothing will wake it: read what it shows through its bound field, or drop the binding`,
        );
      }
    });
  }

  private record(key: string): void {
    if (this.keys.has(key)) return;
    this.keys.add(key);
    if (this.connected) this.registry.addReader(key, this);
  }

  private recordAll(): void {
    if (this.all) return;
    this.all = true;
    if (this.connected) this.registry.addReaderOfEverything(this);
  }
}
